import {
  clearCognitoSessionStorage,
  hasCognitoSession,
} from "src/domains/shared/utils/injectCognitoSession";
import { LOGIN_CLAIM_PENDING_KEY } from "src/domains/shared/utils/storedSessionKeys";
import {
  readExclusiveLoginRecord,
  removeExclusiveLoginRecord,
  writeExclusiveLoginRecord,
} from "./exclusiveLoginStorage";

/**
 * 점유 대기 표식 (design D4).
 *
 * 생산자(비밀번호 로그인은 `Auth.signIn` 직전, SSO 콜백은 토큰 주입 직전)가 쓰고, 점유가 끝나면(진행·취소·실패)
 * 지운다. 표식이 있는 동안 fetch 관문은 모든 탭에서 점유 요청 외의 main-api 요청을 네트워크 없이 거절한다.
 * 점유 전에 나간 요청 하나가 곧 경고 없는 인수(암묵 인수)이기 때문이다.
 *
 * sessionStorage 가 아니라 localStorage 에 둔다. 탭마다 따로인 저장소로는 다른 탭의 요청을 막지 못한다.
 * 키 이름은 문서 머리 부트 스크립트(사이트 머리말의 첫 화면 로그인 판정)와 함께 쓰려고 리프 모듈(`storedSessionKeys`)에 둔다.
 */

/**
 * 남은 점유 대기를 취소한다: 네트워크 없이 Cognito 세션 키를 지운 뒤 표식을 지운다(해제 없음). 취소했으면 true,
 * 표식이 없었으면 아무것도 하지 않고 false 다. 로드 시점의 부팅(표식이 있으면 누가 썼든)과, 표식을 쓴 탭의
 * `pagehide`(표식이 그 탭이 쓴 값 그대로일 때만)가 함께 쓴다.
 */
function cancelPendingClaim(): boolean {
  if (!isLoginClaimPending()) return false;
  clearCognitoSessionStorage();
  clearLoginClaimPending();
  return true;
}

/** 이 탭이 쓴 표식 값. 이 탭의 `pagehide` 취소가 걸려 있는 동안만 있다. */
let ownPendingRecord: string | null = null;

/**
 * 표식을 쓴 탭이 점유가 끝나기 전에 페이지를 떠나면(닫기·이동·새로고침) 그 로그인을 취소한다(스펙 「점유 대기 중
 * 페이지를 떠나면 그 로그인을 취소한다」). 그러지 않으면 표식과 새 토큰이 남아, 이미 열려 있던 다른 탭은 세션 키를
 * 보면서도 모든 요청이 관문에서 거절된다.
 *
 * 취소는 표식이 이 탭이 쓴 값 그대로일 때만 한다. 그동안 다른 탭이 표식을 다시 썼으면(부팅 취소 뒤 그 탭의 새
 * 로그인 등) 지우지 않는다 — 지우면 그 탭이 진행 중인 로그인(그 탭의 표식과, 이미 저장됐으면 토큰)을 지운다. 표식이
 * 없을 때도 지우지 않는다. 어느 경우든 리스너는 한 번 불리면 떨어진다 — bfcache 로 돌아온 페이지에 낡은 취소가 남아
 * 뒤의 이탈에서 다시 불리지 않는다.
 */
const cancelOwnPendingClaimOnPageHide = () => {
  const isOwnRecord = readExclusiveLoginRecord(LOGIN_CLAIM_PENDING_KEY) === ownPendingRecord;
  detachPageHideCancel();
  if (isOwnRecord) cancelPendingClaim();
};

function detachPageHideCancel(): void {
  ownPendingRecord = null;
  if (typeof window !== "undefined") window.removeEventListener("pagehide", cancelOwnPendingClaimOnPageHide);
}

/**
 * 점유 대기를 시작한다. 이 탭이 점유를 마치기 전에 떠나면 그 로그인을 취소하도록 `pagehide` 를 건다 — 떠날 때
 * 표식이 이 탭이 쓴 값 그대로가 아니면(다른 탭이 지웠거나 다시 썼으면) 아무것도 지우지 않는다.
 */
export function markLoginClaimPending(): void {
  const record = JSON.stringify({ at: Date.now() });
  writeExclusiveLoginRecord(LOGIN_CLAIM_PENDING_KEY, record);
  if (typeof window === "undefined") return;
  ownPendingRecord = record;
  window.addEventListener("pagehide", cancelOwnPendingClaimOnPageHide);
}

/** 점유 대기를 끝낸다. 이 탭이 건 `pagehide` 취소도 뗀다 — 점유가 끝난 뒤의 이동은 로그인을 지우지 않는다. */
export function clearLoginClaimPending(): void {
  removeExclusiveLoginRecord(LOGIN_CLAIM_PENDING_KEY);
  detachPageHideCancel();
}

export function isLoginClaimPending(): boolean {
  return readExclusiveLoginRecord(LOGIN_CLAIM_PENDING_KEY) !== null;
}

/**
 * 이 브라우저에 점유까지 끝난 로그인이 저장돼 있는가 — 세션 키가 있고 점유 대기가 아니다. 네트워크 없는 동기 판정이라
 * 로그인이 만료됐는지, 서버가 그 로그인을 받아들이는지는 모른다. 문서 머리 부트 스크립트(`SITE_SESSION_BOOT_SCRIPT`)와
 * 같은 판정이다.
 */
export function hasStoredLogin(): boolean {
  return !isLoginClaimPending() && hasCognitoSession();
}

/**
 * 페이지 로드 시점(앱이 로그인 여부를 판정하거나 그리기 전)에 부른다. 표식이 있으면 그 로그인을 취소한다: 네트워크
 * 없이 Cognito 세션 키를 지운 뒤 표식을 지운다. 취소했으면 true, 표식이 없었으면 아무것도 하지 않고 false 다.
 *
 * 표식은 같은 오리진의 모든 탭이 함께 쓰는 값 하나이고 여기서는 있는지만 본다. 그래서 이전 로드가 확인 없이 버린
 * 점유(새로고침·탭 닫기·크래시)와 **다른 탭에서 확인창이 떠 있는 점유를 가리지 못하고 둘 다 취소한다**. 후자의 탭은
 * 확인(`useLoginClaimFlow` 의 `confirm`)을 눌러도 세션이 없어 실패로 끝나고 다시 로그인해야 한다 — 경고 없는
 * 인수보다 안전한 쪽을 택한 경계다(스펙 「확인창 중 새 탭 열기」).
 */
export function cancelLoginClaimPendingAtLoad(): boolean {
  return cancelPendingClaim();
}
